/**
 * @whatItDoes This service deals with the amenities offered on UZURV trips
 */
import { HttpClient } from '@angular/common/http';
import { Inject, Injectable } from '@angular/core';
import { DomSanitizer } from '@angular/platform-browser';
import { CacheKeyConstants } from '@uzurv/core';
// eslint-disable-next-line import/order
import { UzurvRestConfiguration, UzurvRestService, UZURV_REST_CONFIGURATION } from '@uzurv/http';
import { Deserialize, Serialize } from 'cerialize';
import { Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { Amenity, AmenityType } from '../../models';

@Injectable({
  providedIn: 'root'
})
export class AmenityService extends UzurvRestService {
  readonly resourceUrl = 'amenities/';

  constructor(
    httpClient: HttpClient,
    @Inject(UZURV_REST_CONFIGURATION)
    protected uzurvRestConfiguration: UzurvRestConfiguration,
    domSanitizer: DomSanitizer
  ) {
    super(httpClient, uzurvRestConfiguration, domSanitizer);
  }

  /**
   * Returns an array of amenities from the server
   *
   * @param pageNumber
   * @param pageSize
   * @param includeInactive whether or not to include inactive amenities
   * @permission amenity_read
   */
  getAmenities(pageNumber: number = 0, pageSize: number = 20, includeInactive: boolean = false): Observable<Array<Amenity>> {
    return this.get<Array<Amenity>>(
      this.resourceUrl,
      {
        pageNumber: pageNumber,
        pageSize: pageSize,
        includeInactive: includeInactive
      },
      null,
      Amenity
    ).pipe(map(response => Deserialize(response, Amenity)));
  }

  /**
   * Returns a single amenity by ID
   *
   * @param amenityId
   * @permission amenity_read
   */
  findAmenityById(amenityId: number): Observable<Amenity> {
    return this.get<Amenity>(this.resourceUrl + amenityId, null, null, Amenity).pipe(map(response => Deserialize(response, Amenity)));
  }

  /**
   * Flips the 'active' status of an amenity
   *
   * @param amenity
   * @permission system_configuration
   */
  updateAmenityActiveStatus(amenity: Amenity): Observable<Amenity> {
    return this.patch(this.resourceUrl + amenity.id, {
      id: amenity.id,
      active: !amenity.active
    });
  }

  /**
   * PUTS any desired changes on an amenity
   *
   * @param amenity
   * @permission system_configuration
   */
  updateAmenity(amenity: Amenity): Observable<Amenity> {
    return this.put<Amenity>(this.resourceUrl + amenity.id, Serialize(amenity)).pipe(map(response => Deserialize(response, Amenity)));
  }

  /**
   * Creates a new amenity
   *
   * @param amenity
   * @permission system_configuration
   */
  createAmenity(amenity: Amenity): Observable<Amenity> {
    return this.post<Amenity>(this.resourceUrl, Serialize(amenity));
  }

  /**
   * Lists the amenity types available
   *
   * @param includeInactive
   * @permission amenity_read
   */
  getAmenityTypes(includeInactive = false): Observable<Array<AmenityType>> {
    return this.get<Array<AmenityType>>(`${this.resourceUrl}amenityTypes/`, {
      includeInactive: includeInactive
    }).pipe(map(response => Deserialize(response, AmenityType)));
  }

  /**
   * DELETEs an amenity
   *
   * @param amenity
   * @permission system_configuration
   */
  deleteAmenity(amenity: Amenity): Observable<boolean> {
    return this.delete<boolean>(this.resourceUrl + amenity.id);
  }

  /**
   * DELETEs an amenity type
   *
   * @param amenityId
   * @permission system_configuration
   */
  deleteAmenityType(amenityId: number): Observable<boolean> {
    return this.delete<boolean>(`${this.resourceUrl}amenityTypes/${amenityId}`);
  }

  /**
   * Gets a list of amenities stored in the cache.
   * TODO: there doesn't seem to be a used method for storing amenities in the cache.
   */
  getCachedAmenities(): Array<Amenity> {
    return Deserialize(JSON.parse(localStorage.getItem(CacheKeyConstants.AMENITIES)), Amenity);
  }

  /**
   * Categorizes amenities
   *
   * @param amenities
   */
  categorizeAmenities(amenities: Array<Amenity>): { [s: string]: Array<Amenity> } {
    const categorizedAmenities = {};

    amenities.forEach(amenity => {
      if (!categorizedAmenities.hasOwnProperty(amenity.amenityType.name)) {
        categorizedAmenities[amenity.amenityType.name] = [];
      }
      categorizedAmenities[amenity.amenityType.name].push(amenity);
    });

    Object.keys(categorizedAmenities).forEach(key => {
      categorizedAmenities[key] = this.sortAmenitiesByName(categorizedAmenities[key]);
    });
    return categorizedAmenities;
  }

  /**
   * Sorts amenities by their name
   *
   * @param amenities
   */
  sortAmenitiesByName(amenities: Array<Amenity>): Array<Amenity> {
    return amenities.sort((a, b) => (a.name.localeCompare(b.name)));
  }
}
